import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { catalogApi, type ProductDetail } from '@/lib/api';
import { Gallery } from '@/components/Gallery';
import { ProductCard } from '@/components/ProductCard';
import { VariantSelector } from '@/components/VariantSelector';
import { Availability } from '@/components/Availability';
import { Price } from '@/components/Price';
import { Seo } from '@/components/Seo';
import { useCart } from '@/lib/cart-store';
import { useLocale } from '@/hooks/useLocalePath';
import type { JSX } from 'react';

export function ProductPage(): JSX.Element {
  const { t } = useTranslation();
  const locale = useLocale();
  const { slug = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const add = useCart((s) => s.add);
  const [quantity, setQuantity] = useState(1);

  const { data: product, isLoading, isError } = useQuery({
    queryKey: ['product', slug, locale],
    queryFn: () => catalogApi.detail(slug, locale),
  });

  // The selected combination lives in the URL, so a configured product is linkable.
  const selection = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [key, value] of params.entries()) if (!key.startsWith('_')) out[key] = value;
    return out;
  }, [params]);

  const setSelection = (axis: string, value: string): void => {
    const next = new URLSearchParams(params);
    if (next.get(axis) === value) next.delete(axis);
    else next.set(axis, value);
    setParams(next, { replace: true });
  };

  const matched = useMemo(() => {
    if (!product) return null;
    if (product.mode === 'simple') return product.variants[0] ?? null;
    const axes = product.options.map((o) => o.axisSlug);
    if (axes.some((axis) => !selection[axis])) return null;
    return (
      product.variants.find((v) => axes.every((axis) => v.optionValues[axis] === selection[axis])) ?? null
    );
  }, [product, selection]);

  /**
   * Choosing a colour shows that colour. Most multi-variant Adams families ship their own
   * photography per variant; when the selected one has none, the product-level set stands
   * in rather than the gallery emptying.
   */
  const galleryImages = useMemo(() => {
    if (!product) return [];
    const byVariant = (id: string): ProductDetail['images'] =>
      product.images.filter((i) => i.variantId === id);

    // The fully matched variant wins.
    if (matched) {
      const exact = byVariant(matched.id);
      if (exact.length > 0) return exact;
    }

    // Otherwise the first variant consistent with whatever has been chosen so far *that
    // actually has photography* — picking a colour must repaint the gallery immediately
    // rather than waiting for the last axis, and not every variant carries its own photos.
    // This is the whole point of a colour selector on an upholstery catalogue.
    const chosen = Object.entries(selection).filter(([, value]) => value);
    if (chosen.length > 0) {
      const candidates = product.variants.filter((v) =>
        chosen.every(([axis, value]) => v.optionValues[axis] === value),
      );
      for (const candidate of candidates) {
        const partial = byVariant(candidate.id);
        if (partial.length > 0) return partial;
      }
    }

    const productLevel = product.images.filter((i) => i.variantId === null);
    return productLevel.length > 0 ? productLevel : product.images;
  }, [product, matched, selection]);

  if (isLoading) return <ProductSkeleton />;
  if (isError || !product) {
    return (
      <div className="rz-shell py-32 text-center space-y-3">
        <h1 className="font-display text-xl">{t('errors.productNotFound')}</h1>
        <p className="text-sm text-[var(--color-muted)]">{t('errors.notFoundBody')}</p>
      </div>
    );
  }

  const displayPrice = matched?.priceGross ?? product.minPriceGross;
  const availability = matched?.availability ?? {
    state: 'made_to_order' as const,
    quantity: null,
    leadTimeDays: product.leadTimeDays,
  };
  const canOrder = matched !== null && matched.priceGross !== null && availability.state !== 'unavailable';

  return (
    <>
      <Seo
        title={product.seoTitle ?? product.name}
        description={product.seoDescription ?? product.description.slice(0, 155)}
        path={`/produkt/${product.slug}`}
        image={product.images[0]?.full ?? null}
        structuredData={structuredData(product, displayPrice, availability.state, locale)}
      />

      <div className="rz-shell grid min-w-0 gap-10 py-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:gap-16">
        <Gallery
          images={galleryImages}
          name={product.name}
          selectionKey={galleryImages[0]?.full ?? JSON.stringify(selection)}
        />

        <div className="space-y-8 lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-8rem)] lg:overflow-auto lg:pr-2">
          <div className="space-y-3">
            {product.category && <p className="rz-eyebrow">{product.category.name}</p>}
            <h1 className="text-xl md:text-2xl">{product.name}</h1>
            <div className="flex flex-wrap items-baseline gap-4">
              <Price minor={displayPrice} from={matched === null && product.minPriceGross !== product.maxPriceGross} className="font-display text-lg" />
              <span className="text-2xs text-[var(--color-muted)]">{t('common.vatIncluded')}</span>
            </div>
            <Availability {...availability} />
          </div>

          {/* Simple mode renders no selector at all — 274 products have one variant. */}
          {product.mode === 'configurator' && (
            <VariantSelector
              options={product.options}
              variants={product.variants}
              selection={selection}
              onSelect={setSelection}
            />
          )}

          <div className="space-y-3" data-testid="buy-block">
            <div className="flex gap-3">
              <label className="sr-only" htmlFor="quantity">{t('product.quantity')}</label>
              <input
                id="quantity"
                type="number"
                min={1}
                max={99}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                className="rz-field w-20 shrink-0 text-center"
              />
              <button
                type="button"
                disabled={!canOrder}
                data-testid="add-to-cart"
                onClick={() => {
                  if (!matched || matched.priceGross === null) return;
                  add({
                    variantId: matched.id,
                    quantity,
                    snapshot: {
                      name: product.name,
                      slug: product.slug,
                      image: product.images[0]?.card ?? null,
                      priceGross: matched.priceGross,
                      options: Object.entries(selection).map(([k, v]) => `${k}: ${v}`).join(', '),
                    },
                  });
                }}
                className="rz-button flex-1"
              >
                {canOrder
                  ? t('product.addToCart')
                  : matched === null
                    ? t('product.selectOptions')
                    : t('product.outOfStock')}
              </button>
            </div>
            {availability.state === 'made_to_order' && (
              <p className="text-2xs text-[var(--color-muted)] leading-relaxed">
                {t('product.madeToOrderNote', { min: product.leadTimeDays[0], max: product.leadTimeDays[1] })}
              </p>
            )}
          </div>

          {product.description && (
            <section className="space-y-2">
              <h2 className="rz-eyebrow">{t('product.description')}</h2>
              {/* Never rendered as HTML: supplier text is untrusted input, sanitized on import. */}
              <p className="whitespace-pre-line text-sm leading-relaxed text-[var(--color-ink-soft)]">
                {product.description}
              </p>
              {product.descriptionIsFallback && (
                <p className="text-2xs text-[var(--color-muted)] italic">{t('product.sourceLanguageNote')}</p>
              )}
            </section>
          )}

          <Specification product={product} />
        </div>
      </div>

      <RelatedProducts slug={product.slug} categorySlug={product.category?.slug ?? null} />

      {/* On a phone the buy action sits a full screen below the gallery, so it follows the
          customer down the page instead. Hidden while the real one is on screen. */}
      <MobileBuyBar
        name={product.name}
        price={displayPrice}
        canOrder={canOrder}
        label={
          canOrder ? t('product.addToCart') : matched === null ? t('product.selectOptions') : t('product.outOfStock')
        }
        onAdd={() => {
          if (!matched || matched.priceGross === null) return;
          add({
            variantId: matched.id,
            quantity,
            snapshot: {
              name: product.name,
              slug: product.slug,
              image: product.images[0]?.card ?? null,
              priceGross: matched.priceGross,
              options: Object.entries(selection).map(([k, v]) => `${k}: ${v}`).join(', '),
            },
          });
        }}
      />

      {/* Absent, not empty, on the 1302 products with no mechanism media. */}
      {product.mechanism && (
        <section className="bg-[var(--color-ground-deep)] py-16" aria-labelledby="mechanism">
          <div className="rz-shell grid gap-8 md:grid-cols-[1fr_1.2fr] md:items-center">
            <div className="space-y-3">
              <p className="rz-eyebrow">{t('product.howItUnfolds')}</p>
              <h2 id="mechanism" className="text-lg">{t('product.howItUnfolds')}</h2>
              <p className="text-sm text-[var(--color-ink-soft)] leading-relaxed">{t('product.howItUnfoldsLead')}</p>
            </div>
            <img
              src={product.mechanism.gif ?? product.mechanism.image ?? ''}
              alt={`${product.name} — ${t('product.howItUnfolds')}`}
              loading="lazy"
              className="w-full bg-[var(--color-ground-lift)] object-contain"
              data-testid="mechanism-media"
            />
          </div>
        </section>
      )}
    </>
  );
}

/**
 * Appears once the in-page buy block has scrolled away, so the price and the action stay
 * reachable on a long mobile product page. Desktop never sees it — the block is sticky there.
 */
/**
 * More from the same category.
 *
 * A furniture shop is browsed sideways — someone looking at one bedside table wants to see
 * the other bedside tables, not to go back to a grid. It is also the clearest proof that
 * the taxonomy is right: if this rail shows a sofa under a coffee table, the categories are
 * wrong, and that is visible to anyone rather than only in a query.
 */
function RelatedProducts({
  slug,
  categorySlug,
}: {
  slug: string;
  categorySlug: string | null;
}): JSX.Element | null {
  const { t } = useTranslation();
  const locale = useLocale();
  const { data } = useQuery({
    queryKey: ['related', categorySlug, locale],
    queryFn: () => catalogApi.list({ locale, category: categorySlug ?? undefined, pageSize: 10, page: 1 }),
    enabled: Boolean(categorySlug),
  });

  const items = (data?.items ?? []).filter((p) => p.slug !== slug).slice(0, 4);
  if (items.length === 0) return null;

  return (
    <section className="rz-shell pb-20" aria-labelledby="related-heading">
      <h2 id="related-heading" className="rz-eyebrow mb-5">
        {t('product.related')}
      </h2>
      <div className="grid grid-cols-2 gap-x-4 gap-y-10 md:grid-cols-4">
        {items.map((p, i) => (
          <ProductCard key={p.id} product={p} index={i} />
        ))}
      </div>
    </section>
  );
}

function MobileBuyBar({
  name, price, canOrder, label, onAdd,
}: {
  name: string;
  price: number | null;
  canOrder: boolean;
  label: string;
  onAdd: () => void;
}): JSX.Element {
  const [visible, setVisible] = useState(false);

  // A scroll listener rather than an IntersectionObserver: fewer moving parts, no
  // rootMargin subtleties, and it behaves identically in every environment.
  useEffect(() => {
    // A plain scroll listener, measured synchronously. `IntersectionObserver` and
    // `requestAnimationFrame` both go quiet when the page is not being painted, which
    // makes them untestable and, on a backgrounded tab, unreliable.
    const measure = (): void => {
      const target = document.querySelector('[data-testid="buy-block"]');
      if (!target) return;
      // Show it once the real buy block has scrolled off the top of the viewport.
      setVisible(target.getBoundingClientRect().bottom < 72);
    };
    measure();
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }}
          transition={{ type: 'spring', stiffness: 320, damping: 34 }}
          className="fixed inset-x-0 bottom-0 z-40 border-t border-[var(--color-line)] bg-[var(--color-ground-lift)] lg:hidden"
          data-testid="mobile-buy-bar"
        >
          <div className="rz-shell flex items-center gap-3 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="truncate text-2xs text-[var(--color-muted)]">{name}</p>
              <Price minor={price} className="text-sm font-semibold" />
            </div>
            <button type="button" disabled={!canOrder} onClick={onAdd} className="rz-button shrink-0 !py-3">
              {label}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Specification({ product }: { product: ProductDetail }): JSX.Element {
  const { t } = useTranslation();
  const spec = product.specification;
  const rows: Array<[string, string]> = [];
  if (spec.widthCm) rows.push([t('product.width'), `${spec.widthCm} cm`]);
  if (spec.depthCm) rows.push([t('product.depth'), `${spec.depthCm} cm`]);
  if (spec.heightCm) rows.push([t('product.height'), `${spec.heightCm} cm`]);
  if (spec.weightKg) rows.push([t('product.weight'), `${spec.weightKg} kg`]);
  if (spec.ean) rows.push([t('product.ean'), spec.ean]);

  return (
    <section className="space-y-3">
      <h2 className="rz-eyebrow">{t('product.specification')}</h2>
      <dl className="text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between py-1.5 border-b border-[var(--color-line)]">
            <dt className="text-[var(--color-muted)]">{label}</dt>
            <dd className="font-medium tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>

      {/* A 3-parcel sofa is information a customer wants before ordering. */}
      {spec.parcels.length > 0 && (
        <div className="space-y-1.5 pt-2">
          <p className="text-xs font-semibold">{t('product.parcelCount', { count: spec.parcels.length })}</p>
          <ul className="space-y-0.5">
            {spec.parcels.map((parcel) => (
              <li key={parcel.index} className="text-2xs text-[var(--color-muted)] tabular-nums">
                {t('product.parcelDetail', {
                  index: parcel.index,
                  w: parcel.widthCm ?? '—',
                  d: parcel.depthCm ?? '—',
                  h: parcel.heightCm ?? '—',
                  kg: parcel.weightKg ?? '—',
                })}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function ProductSkeleton(): JSX.Element {
  return (
    <div className="rz-shell grid gap-10 py-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
      <div className="rz-skeleton aspect-square" />
      <div className="space-y-4">
        <div className="rz-skeleton h-3 w-24" />
        <div className="rz-skeleton h-8 w-3/4" />
        <div className="rz-skeleton h-6 w-32" />
        <div className="rz-skeleton h-24 w-full" />
        <div className="rz-skeleton h-12 w-full" />
      </div>
    </div>
  );
}

/** Product/Offer structured data with price, currency and availability, per locale. */
function structuredData(
  product: ProductDetail,
  price: number | null,
  availability: string,
  locale: string,
): Record<string, unknown> {
  const AVAILABILITY_URL: Record<string, string> = {
    in_stock: 'https://schema.org/InStock',
    low_stock: 'https://schema.org/LimitedAvailability',
    made_to_order: 'https://schema.org/PreOrder',
    unavailable: 'https://schema.org/OutOfStock',
  };
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description.slice(0, 400),
    sku: product.variants[0]?.sku,
    gtin13: product.specification.ean ?? undefined,
    image: product.images.slice(0, 5).map((i) => i.full).filter(Boolean),
    brand: { '@type': 'Brand', name: product.supplier.name },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'PLN',
      price: price === null ? undefined : (price / 100).toFixed(2),
      availability: AVAILABILITY_URL[availability],
      url: `${typeof window !== 'undefined' ? window.location.origin : ''}/${locale}/produkt/${product.slug}`,
      itemCondition: 'https://schema.org/NewCondition',
    },
    ...(product.category
      ? {
          isRelatedTo: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: product.category.name },
              { '@type': 'ListItem', position: 2, name: product.name },
            ],
          },
        }
      : {}),
  };
}
